import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { usePage, router } from "@inertiajs/react";
import { TooltipProvider } from "@/components/ui/Tooltip";
import { FlashAlert } from "@/components/Flash/FlashAlert";
import { FlashInfo } from "@/components/Flash/FlashInfo";
import { FlashNotice } from "@/components/Flash/FlashNotice";
import ImpersonationBanner, { type Impersonating } from "@/components/ImpersonationBanner";
import PageTabs from "@/components/layout/PageTabs";
import TopBar from "@/components/layout/TopBar";
import BookmarksBar from "@/components/layout/bookmarks/BookmarksBar";
import ProductQuickViewHost from "@/components/productQuickView/ProductQuickViewHost";

const FLASH_COMPONENTS = { alert: FlashAlert, info: FlashInfo, notice: FlashNotice };

interface AppLayoutProps {
  children: React.ReactNode;
  header?: React.ReactNode;
  headerActions?: React.ReactNode;
  // Full-bleed content pane (no padding). Spreadsheet pages need this: sticky
  // offsets inside the scroll pane resolve against its content box, so any
  // pane padding misplaces sticky headers/columns by the padding amount.
  flush?: boolean;
}

interface PageProps {
  flash?: Record<string, string>;
  impersonating?: Impersonating | null;
  debug?: { controller?: string };
  [key: string]: unknown;
}

// Inertia fires one "navigate" event for the initial full page load, after AppLayout
// has mounted. The flash effect below clears the toast on navigate, so without this
// flag a flash delivered by a full page load (plain link → server redirect with an
// alert) is wiped before anyone sees it. Module scope: reset by a full page load,
// kept across in-app page changes, so only that first event is ignored.
let initialNavigateSeen = false;

export default function AppLayout({ children, header, headerActions, flush = false }: AppLayoutProps) {
  const { url, props, component } = usePage<PageProps>();
  const debugController = props.debug?.controller || "";
  const debugControllerPath = debugController.split("#")[0]
    ? `app/controllers/${debugController.split("#")[0].replace(/([A-Z])/g, "_$1").toLowerCase().replace(/^_/, "")}.rb`
    : "";
  const debugReactPath = `app/javascript/pages/${component}.tsx`;
  const [visibleFlash, setVisibleFlash] = useState<{ type: "alert" | "info" | "notice"; message: string; link?: string; linkLabel?: string } | null>(null);
  // Visible height of the scroll pane and of the title band, exposed as
  // --pane-height / --title-band-height so a panel beside a scrolling list can
  // fit the screen on load and stay pinned (My Work details).
  const paneRef = useRef<HTMLDivElement>(null);
  const titleBandRef = useRef<HTMLDivElement>(null);
  const [paneSizes, setPaneSizes] = useState({ pane: 0, titleBand: 0 });
  useLayoutEffect(() => {
    const pane = paneRef.current;
    if (!pane) return;
    const band = titleBandRef.current;
    const measure = () => setPaneSizes({ pane: pane.clientHeight, titleBand: band?.offsetHeight ?? 0 });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(pane);
    if (band) observer.observe(band);
    return () => observer.disconnect();
  }, []);

  // Flash toast, driven purely by Inertia router events (verified order):
  //   • real server visit → NAVIGATE (if page changes) then SUCCESS — SUCCESS always last
  //   • Back/Forward cache-restore → NAVIGATE only (no server round-trip, no SUCCESS)
  //   • initial full page load → mount, then one NAVIGATE (no SUCCESS)
  // So: NAVIGATE clears, SUCCESS sets. Because SUCCESS fires after NAVIGATE on a real
  // visit, clearing-on-navigate never eats a fresh flash; but a Back (navigate-only)
  // clears the stale toast the user already saw. The mount check covers the initial
  // full page load; its own NAVIGATE is skipped (initialNavigateSeen) so it does not
  // clear the flash the mount check just set.
  useEffect(() => {
    const toFlash = (f?: Record<string, string>) => {
      const message = f && (f.notice || f.alert || f.info);
      return message
        ? {
            type: (f!.alert ? "alert" : f!.info ? "info" : "notice") as "alert" | "info" | "notice",
            message,
            link: f!.link,
            linkLabel: f!.link_label,
          }
        : null;
    };
    setVisibleFlash(toFlash(props.flash));
    const offNav = router.on("navigate", () => {
      if (!initialNavigateSeen) {
        initialNavigateSeen = true;
        return;
      }
      setVisibleFlash(null);
    });
    const offOk = router.on("success", (e) => setVisibleFlash(toFlash(e.detail?.page?.props?.flash as Record<string, string> | undefined)));
    return () => { offNav(); offOk(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TooltipProvider delayDuration={100} skipDelayDuration={100}>
    <div className="app-layout" style={{ display: "flex", flexDirection: "column", height: "100vh", fontFamily: "'Geist Sans', 'Segoe UI', system-ui, -apple-system, sans-serif", background: "var(--page-background)", color: "var(--foreground)", overflow: "hidden" }}>
      <link href="https://fonts.googleapis.com/css2?family=Geist+Mono:wght@400;500&display=swap" rel="stylesheet" />
      {props.impersonating && <ImpersonationBanner impersonating={props.impersonating} />}
      {import.meta.env.DEV && (
        <div style={{ background: "var(--foreground)", color: "var(--background)", fontSize: 10, padding: "2px 12px", fontFamily: "'Geist Mono', monospace", flexShrink: 0, display: "flex", gap: 8, justifyContent: "center" }}>
          <span
            style={{ cursor: "pointer", color: "var(--chart-2)" }}
            onClick={() => { const json = JSON.stringify({ url, controller: debugController, react: debugReactPath }, null, 2); const el = document.createElement("textarea"); el.value = json; document.body.appendChild(el); el.select(); document.execCommand("copy"); document.body.removeChild(el); }}
            title={`Click to copy JSON: { url: "${url}", controller: "${debugController}", react: "${debugReactPath}" }`}
          >
            Debug:
          </span>
          <span
            style={{ cursor: "pointer" }}
            onClick={() => { const el = document.createElement("textarea"); el.value = debugControllerPath; document.body.appendChild(el); el.select(); document.execCommand("copy"); document.body.removeChild(el); }}
            title={`Click to copy: ${debugControllerPath}`}
          >
            {debugController}
          </span>
          <span style={{ color: "var(--muted-foreground)" }}>→</span>
          <span
            style={{ cursor: "pointer" }}
            onClick={() => { const el = document.createElement("textarea"); el.value = debugReactPath; document.body.appendChild(el); el.select(); document.execCommand("copy"); document.body.removeChild(el); }}
            title={`Click to copy: ${debugReactPath}`}
          >
            {debugReactPath}
          </span>
        </div>
      )}

      {/* Main */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0, minHeight: 0 }}>
        <TopBar />
        <BookmarksBar />

        {/* Scroll pane. position:relative so absolutely-positioned descendants (e.g. the
            1px hidden native <select> Radix Select renders for form autofill) anchor
            inside this pane instead of <html> — otherwise they escape the overflow
            clip and stretch the document, adding a phantom outer scrollbar. */}
        <div
          ref={paneRef}
          style={{
            position: "relative",
            flex: 1,
            overflow: "auto",
            ["--pane-height" as string]: `${paneSizes.pane}px`,
            ["--title-band-height" as string]: `${paneSizes.titleBand}px`,
          }}
        >
          {/* Page title band, with the Menu section's tabs next to the title
              (PageTabs). Inside the pane so it scrolls away with the page;
              sticky grid headers stick to the pane's top edge. */}
          {(header || headerActions) && (
            <div ref={titleBandRef} className="flex flex-wrap items-center justify-between gap-3 px-6 pb-3 pt-4">
              <div className="flex min-w-0 flex-wrap items-center gap-x-5 gap-y-2">
                {header}
                <PageTabs />
              </div>
              {headerActions && <div className="flex items-center gap-2.5">{headerActions}</div>}
            </div>
          )}
          <div style={{ padding: flush ? 0 : header || headerActions ? "0 24px 20px" : "20px 24px" }}>
            {children}
          </div>
        </div>
      </div>

      {visibleFlash && createPortal(
        (() => {
          const FlashCmp = FLASH_COMPONENTS[visibleFlash.type];
          return (
            <FlashCmp
              message={visibleFlash.message}
              link={visibleFlash.link}
              linkLabel={visibleFlash.linkLabel}
              onDismiss={() => setVisibleFlash(null)}
            />
          );
        })(),
        document.body
      )}

      <ProductQuickViewHost />

      <style>{`
        .app-layout ::-webkit-scrollbar { width: 6px; }
        .app-layout ::-webkit-scrollbar-track { background: transparent; }
        .app-layout ::-webkit-scrollbar-thumb { background: var(--chart-muted); border-radius: 3px; }
        .app-layout ::-webkit-scrollbar-thumb:hover { background: var(--muted-foreground); }
        .app-layout button { font-family: inherit; }
      `}</style>
    </div>
    </TooltipProvider>
  );
}
